import React, { useState, useEffect, useCallback } from 'react';
import { connectAuthPost, setConnectUser, getConnectUser } from './connectApi';
import ConnectSelect from './ConnectSelect';
import './styles/TravelPlans.css';

/**
 * Where a member lives, and where they are going.
 *
 * The home base is separate from the destination they happen to be browsing:
 * it is what earns the Local badge when someone opens Meet for that place, so
 * it has to stay put while they travel.
 *
 * Trips are held one per destination, so a member can have several at once —
 * Curaçao in March and Suriname in May are two rows, not a replacement.
 */

const today = () => new Date().toISOString().slice(0, 10);

const formatRange = (arrival, departure) => {
  if (!arrival || !departure) return '';
  const opts = { day: 'numeric', month: 'short' };
  const a = new Date(`${arrival}T12:00:00`);
  const d = new Date(`${departure}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(d.getTime())) return '';
  const sameYear = a.getFullYear() === d.getFullYear();
  const year = sameYear ? ` ${d.getFullYear()}` : '';
  return `${a.toLocaleDateString('en-GB', opts)} – ${d.toLocaleDateString('en-GB', opts)}${year}`;
};

const nights = (arrival, departure) => {
  const a = new Date(`${arrival}T12:00:00`);
  const d = new Date(`${departure}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(d.getTime())) return 0;
  return Math.max(0, Math.round((d - a) / 86400000));
};

const EMPTY_TRIP = {
  destination_id: '',
  arrival: '',
  departure: '',
  open_to_connect: true,
  show_exact_dates: true,
};

export default function TravelPlans({ onHomeChange }) {
  const [destinations, setDestinations] = useState([]);
  const [trips, setTrips] = useState([]);
  const [homeId, setHomeId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingHome, setIsSavingHome] = useState(false);
  const [homeSaved, setHomeSaved] = useState(false);
  const [form, setForm] = useState(EMPTY_TRIP);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadTrips = useCallback(async () => {
    const result = await connectAuthPost('connect/trips/list');
    if (result.success) setTrips(result.data.trips || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [destRes, profileRes] = await Promise.all([
          connectAuthPost('connect/destinations/list'),
          connectAuthPost('connect/profile/get'),
        ]);
        if (cancelled) return;

        if (destRes.success) setDestinations(destRes.data.destinations || []);
        if (profileRes.success) {
          setHomeId(profileRes.data.user.home_destination_id || '');
        }
        await loadTrips();
      } catch (err) {
        if (!cancelled) setError('Could not load your travel plans.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [loadTrips]);

  const handleHomeChange = async (value) => {
    setHomeId(value);
    setIsSavingHome(true);
    setHomeSaved(false);
    setError('');
    try {
      const result = await connectAuthPost('connect/profile/update', {
        home_destination_id: value === '' ? null : Number(value),
      });
      if (result.success) {
        // The cached session is the camelCase shape, so it is keyed to match
        const user = getConnectUser();
        if (user) {
          setConnectUser({ ...user, homeDestinationId: result.data.user.home_destination_id });
        }
        setHomeSaved(true);
        if (onHomeChange) onHomeChange(result.data.user.home_destination_id ?? null);
      } else {
        setError(result.response || 'Could not save where you are based.');
      }
    } catch (err) {
      setError('Could not reach the server.');
    } finally {
      setIsSavingHome(false);
    }
  };

  const handleAddTrip = async (e) => {
    e.preventDefault();
    if (busy) return;

    if (!form.destination_id || !form.arrival || !form.departure) {
      setError('Pick a destination and both dates.');
      return;
    }
    if (form.departure < form.arrival) {
      setError('The departure date is before the arrival date.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const result = await connectAuthPost('connect/trips/save', {
        destination_id: Number(form.destination_id),
        arrival: form.arrival,
        departure: form.departure,
        open_to_connect: form.open_to_connect,
        show_exact_dates: form.show_exact_dates,
      });
      if (result.success) {
        setForm(EMPTY_TRIP);
        setShowForm(false);
        await loadTrips();
      } else {
        setError(result.response || 'Could not save that trip.');
      }
    } catch (err) {
      setError('Could not reach the server.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (trip) => {
    if (!window.confirm(`Remove your trip to ${trip.destination_name}?`)) return;
    setBusy(true);
    setError('');
    try {
      const result = await connectAuthPost('connect/trips/end', { trip_id: trip.id });
      if (result.success) await loadTrips();
      else setError(result.response || 'Could not remove that trip.');
    } catch (err) {
      setError('Could not reach the server.');
    } finally {
      setBusy(false);
    }
  };

  const homeName = destinations.find(d => String(d.id) === String(homeId))?.name || '';

  // A destination already planned is edited from its own row, not added twice
  const plannedIds = trips.map(t => String(t.destination_id));

  if (isLoading) {
    return <div className="tp-panel"><p className="tp-loading">Loading your travel plans...</p></div>;
  }

  return (
    <div className="tp-panel">
      <section className="tp-section">
        <h3 className="tp-title">Where are you based?</h3>
        <p className="tp-hint">
          People browsing this destination will see a <strong>Local</strong> badge
          on your profile. It stays put while you travel.
        </p>

        <ConnectSelect
          id="tp-home"
          ariaLabel="Where you are based"
          value={homeId || ''}
          placeholder="Not set"
          options={[
            { value: '', label: 'Prefer not to say' },
            ...destinations.map(d => ({ value: d.id, label: d.name })),
          ]}
          onChange={handleHomeChange}
        />

        {isSavingHome && <p className="tp-note">Saving...</p>}
        {homeSaved && !isSavingHome && homeName && (
          <p className="tp-note tp-note-ok">You show as a local in {homeName}.</p>
        )}
      </section>

      <section className="tp-section">
        <div className="tp-section-head">
          <h3 className="tp-title">Your trips</h3>
          {!showForm && (
            <button type="button" className="tp-add" onClick={() => { setShowForm(true); setError(''); }}>
              Add a trip
            </button>
          )}
        </div>
        <p className="tp-hint">
          Tell people when you will be somewhere, so they can meet you while you
          are there. You can have one trip per destination at a time. Anyone who
          can see your profile can see your trips, so your privacy setting applies.
        </p>

        {error && <p className="tp-error">{error}</p>}

        {trips.length === 0 && !showForm && (
          <p className="tp-empty">No trips planned yet.</p>
        )}

        {trips.length > 0 && (
          <ul className="tp-list">
            {trips.map(trip => (
              <li key={trip.id} className={`tp-trip ${trip.status === 'active' ? 'is-now' : ''}`}>
                <div className="tp-trip-main">
                  <strong className="tp-trip-place">{trip.destination_name}</strong>
                  <span className="tp-trip-dates">
                    {formatRange(trip.arrival, trip.departure)}
                    {' · '}
                    {nights(trip.arrival, trip.departure)} night
                    {nights(trip.arrival, trip.departure) === 1 ? '' : 's'}
                  </span>
                  <span className="tp-trip-flags">
                    {trip.status === 'active' ? 'There now' : 'Upcoming'}
                    {!trip.open_to_connect && ' · Not open to meeting'}
                    {!trip.show_exact_dates && ' · Dates hidden'}
                  </span>
                </div>
                <button
                  type="button"
                  className="tp-remove"
                  onClick={() => handleRemove(trip)}
                  disabled={busy}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {showForm && (
          <form className="tp-form" onSubmit={handleAddTrip}>
            <div className="tp-field">
              <label htmlFor="tp-dest">Going to</label>
              <ConnectSelect
                id="tp-dest"
                ariaLabel="Destination"
                value={form.destination_id}
                placeholder="Choose a destination"
                options={destinations.map(d => ({
                  value: d.id,
                  label: plannedIds.includes(String(d.id))
                    ? `${d.name} (replaces your current trip)`
                    : d.name,
                }))}
                onChange={(v) => setForm(prev => ({ ...prev, destination_id: v }))}
              />
            </div>

            <div className="tp-dates">
              <div className="tp-field">
                <label htmlFor="tp-arrival">Arriving</label>
                <input
                  id="tp-arrival"
                  type="date"
                  min={today()}
                  value={form.arrival}
                  onChange={(e) => setForm(prev => ({ ...prev, arrival: e.target.value }))}
                />
              </div>
              <div className="tp-field">
                <label htmlFor="tp-departure">Leaving</label>
                <input
                  id="tp-departure"
                  type="date"
                  min={form.arrival || today()}
                  value={form.departure}
                  onChange={(e) => setForm(prev => ({ ...prev, departure: e.target.value }))}
                />
              </div>
            </div>

            <label className="tp-check">
              <input
                type="checkbox"
                checked={form.open_to_connect}
                onChange={(e) => setForm(prev => ({ ...prev, open_to_connect: e.target.checked }))}
              />
              I am open to meeting people on this trip
            </label>

            <label className="tp-check">
              <input
                type="checkbox"
                checked={form.show_exact_dates}
                onChange={(e) => setForm(prev => ({ ...prev, show_exact_dates: e.target.checked }))}
              />
              Show my exact dates
              <span className="tp-check-hint">
                Off shows something vaguer, like "here this week".
              </span>
            </label>

            <div className="tp-form-actions">
              <button
                type="button"
                className="tp-cancel"
                onClick={() => { setShowForm(false); setForm(EMPTY_TRIP); setError(''); }}
                disabled={busy}
              >
                Cancel
              </button>
              <button type="submit" className="tp-save" disabled={busy}>
                {busy ? 'Saving...' : 'Save trip'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
